import { useCallback, useState } from "react";

import {
  ComplianceService,
  type EvidenceReferenceResponse,
  type EvidenceType,
  type ManualEvidenceRequest,
} from "@/api";
import { useMutation } from "@/hooks/use-api";
import { useTranslation } from "@/i18n";
import type { TranslationKey } from "@/i18n";
import { parseApiError } from "@/lib/api-error";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { FormField } from "@/components/ui/form-field";
import { cn } from "@/lib/utils";

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const TYPES: ReadonlyArray<EvidenceType> = [
  "policy_version",
  "asset",
  "risk_snapshot",
  "audit_log",
  "framework_alignment",
];

// Evidence kinds map to translation keys so the type picker
// re-renders on a locale switch.
const EVIDENCE_TYPE_KEY: Record<EvidenceType, TranslationKey> = {
  policy_version: "compliance.evidenceType.policy_version",
  asset: "compliance.evidenceType.asset",
  risk_snapshot: "compliance.evidenceType.risk_snapshot",
  audit_log: "compliance.evidenceType.audit_log",
  framework_alignment: "compliance.evidenceType.framework_alignment",
};

export function ComplianceEvidenceSubmitDialog({
  open,
  onClose,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [clauseId, setClauseId] = useState("");
  const [evidenceType, setEvidenceType] =
    useState<EvidenceType>("policy_version");
  const [referenceId, setReferenceId] = useState("");
  const [expiresAt, setExpiresAt] = useState("");

  const mutator = useCallback(
    (body: ManualEvidenceRequest) => ComplianceService.submitEvidence(body),
    []
  );

  const { mutate, isLoading, error, reset } = useMutation<
    ManualEvidenceRequest,
    EvidenceReferenceResponse
  >(mutator, {
    onSuccess: () => {
      setClauseId("");
      setReferenceId("");
      setExpiresAt("");
      setEvidenceType("policy_version");
      onSuccess();
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clauseId.trim() || !referenceId.trim()) return;
    const body: ManualEvidenceRequest = {
      clause_id: clauseId.trim(),
      evidence_type: evidenceType,
      reference_id: referenceId.trim(),
    };
    if (expiresAt) body.expires_at = expiresAt;
    await mutate(body);
  };

  return (
    <Dialog open={open} onClose={handleClose} size="max-w-lg">
      <DialogHeader>
        <DialogTitle>
          {t("compliance.evidenceSubmitDialog.title")}
        </DialogTitle>
        <DialogDescription>
          {t("compliance.evidenceSubmitDialog.description")}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={handleSubmit}>
        <DialogBody>
          <FormField
            label={t("compliance.evidenceSubmitDialog.clauseIdLabel")}
            value={clauseId}
            onChange={setClauseId}
            placeholder={t(
              "compliance.evidenceSubmitDialog.clauseIdPlaceholder"
            )}
            required
            autoFocus
          />

          <div className="space-y-1.5">
            <label
              htmlFor="evidence-type"
              className="block text-sm font-medium text-ocean-900"
            >
              {t("compliance.evidenceSubmitDialog.typeLabel")}
            </label>
            <select
              id="evidence-type"
              value={evidenceType}
              onChange={(e) => setEvidenceType(e.target.value as EvidenceType)}
              className={cn(
                "h-10 w-full rounded-md border border-ocean-100 bg-white px-3 text-sm",
                "focus:outline-none focus:ring-2 focus:ring-ocean-400"
              )}
            >
              {TYPES.map((type) => (
                <option key={type} value={type}>
                  {t(EVIDENCE_TYPE_KEY[type])}
                </option>
              ))}
            </select>
          </div>

          <FormField
            label={t("compliance.evidenceSubmitDialog.referenceIdLabel")}
            value={referenceId}
            onChange={setReferenceId}
            placeholder={t(
              "compliance.evidenceSubmitDialog.referenceIdPlaceholder"
            )}
            required
            hint={t("compliance.evidenceSubmitDialog.referenceIdHint")}
          />

          <FormField
            label={t("compliance.evidenceSubmitDialog.expiresAtLabel")}
            type="datetime-local"
            value={expiresAt}
            onChange={setExpiresAt}
            hint={t("compliance.evidenceSubmitDialog.expiresAtHint")}
          />

          {error && <Alert variant="danger">{parseApiError(error)}</Alert>}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading}
          >
            {t("compliance.evidenceSubmitDialog.cancel")}
          </Button>
          <Button
            type="submit"
            disabled={
              isLoading || !clauseId.trim() || !referenceId.trim()
            }
          >
            {isLoading
              ? t("compliance.evidenceSubmitDialog.submitLoading")
              : t("compliance.evidenceSubmitDialog.submit")}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
